import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSignOutAlt,
  faCog,
  faMessage,
  faBars,
  faClock,
  faCalendar,
  faChevronDown,
  faBuilding,
  faIdBadge,
  faTimes,
  faHeadset,
  faWallet,
} from '@fortawesome/free-solid-svg-icons';

import { useUserContext } from '../UserContext';
import { useEntidadeContext } from '../EntidadeContext';
import logoFxcImage from '../img/logofxc.png';
import FontSizeControl from './FontSizeControl';

// ── Mapa de cores por perfil ──────────────────────────────────────────────────
const ROLE_THEME = {
  'Representante Legal': {
    gradient: 'linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%)',
    accent: '#3b82f6',
    badge: '#dbeafe',
    badgeText: '#1d4ed8',
  },
  'Médico': {
    gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
    accent: '#10b981',
    badge: '#d1fae5',
    badgeText: '#065f46',
  },
  'Recepção': {
    gradient: 'linear-gradient(135deg, #4c1d95 0%, #7c3aed 100%)',
    accent: '#8b5cf6',
    badge: '#ede9fe',
    badgeText: '#5b21b6',
  },
  default: {
    gradient: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
    accent: '#6b7280',
    badge: '#f3f4f6',
    badgeText: '#374151',
  },
};

// ── Componente de Dropdown customizado ───────────────────────────────────────
const NavDropdown = ({ trigger, children, align = 'right' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        {trigger}
      </div>
      {open && (
        <div onClick={() => setOpen(false)} style={{
          position: 'absolute',
          top: 'calc(100% + 8px)',
          [align === 'right' ? 'right' : 'left']: 0,
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          minWidth: '200px',
          zIndex: 9999,
          overflow: 'hidden',
          animation: 'dropdownFadeIn 0.15s ease',
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

// ── Item de dropdown ──────────────────────────────────────────────────────────
const DropdownItem = ({ to, onClick, icon, label, danger = false }) => {
  const [hovered, setHovered] = useState(false);
  const content = (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '10px 16px', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500',
        color: danger ? '#ef4444' : '#374151',
        backgroundColor: hovered ? (danger ? '#fef2f2' : '#f9fafb') : 'transparent',
        cursor: 'pointer', transition: 'background 0.1s',
      }}
    >
      {icon && <FontAwesomeIcon icon={icon} style={{ width: '14px', fontSize: `calc(12px * var(--font-scale))`, color: danger ? '#ef4444' : '#9ca3af' }} />}
      {label}
    </div>
  );

  if (to) return <Link to={to} style={{ textDecoration: 'none' }}>{content}</Link>;
  return content;
};

const DropdownDivider = () => (
  <div style={{ height: '1px', backgroundColor: '#f3f4f6', margin: '4px 0' }} />
);

// ── Componente principal ──────────────────────────────────────────────────────
const AppNavbar = ({
  selectedModulo,
  onMudarPerfil,
  activeHeaderTabKey,
  setActiveHeaderTabKey,
  onLogout,
  userName,
  loginDateTime,
  permittedHeaderTabs = [],
  pacienteNome,
}) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { currentUserRole, dynamicAccessConfig, isLoadingAccessConfig } = useUserContext();
  const { currentEntidade } = useEntidadeContext();

  const theme = ROLE_THEME[currentUserRole] || ROLE_THEME.default;

  // ── Permissões ──
  const canViewComunicados = useMemo(() => {
    if (isLoadingAccessConfig || !currentUserRole) return false;

    const activeModules = currentEntidade?.modulos_ativos;
    const hasClinicalModule = !Array.isArray(activeModules) || activeModules.includes('consultorio');
    if (!hasClinicalModule) return false;

    const m = Object.values(dynamicAccessConfig).find(
      mod => mod.comunicados && Array.isArray(mod.comunicados.roles)
    );
    return m?.comunicados.roles.includes(currentUserRole) || false;
  }, [currentEntidade?.modulos_ativos, dynamicAccessConfig, currentUserRole, isLoadingAccessConfig]);
  const canViewConfiguracoes = useMemo(() => {
    if (isLoadingAccessConfig || !currentUserRole) return false;
    const m = Object.values(dynamicAccessConfig).find(
      mod => mod.configuracoes && Array.isArray(mod.configuracoes.roles)
    );
    return m?.configuracoes.roles.includes(currentUserRole) || false;
  }, [dynamicAccessConfig, currentUserRole, isLoadingAccessConfig]);

  // ── Formatação de data/hora ──
  const { formattedDate, formattedTime } = useMemo(() => {
    if (!loginDateTime) return { formattedDate: '--/--/----', formattedTime: '--:--' };
    const d = loginDateTime;
    return {
      formattedDate: `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`,
      formattedTime: `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`,
    };
  }, [loginDateTime]);

  const handleTabClick = (key) => {
    setActiveHeaderTabKey(key);
    navigate(`/${key}`);
    setMobileMenuOpen(false);
  };

  const tabs = permittedHeaderTabs.filter(tab => tab.key !== 'navitem');

  const MAX_DIRECT_DESKTOP_TABS = 4;
  const shouldUseMoreMenu = tabs.length > 5;

  const visibleDesktopTabs = shouldUseMoreMenu
    ? tabs.slice(0, MAX_DIRECT_DESKTOP_TABS)
    : tabs;

  const overflowDesktopTabs = shouldUseMoreMenu
    ? tabs.slice(MAX_DIRECT_DESKTOP_TABS)
    : [];

  const activeOverflowTab = overflowDesktopTabs.find(
    tab => tab.key === activeHeaderTabKey
  );

  // ── Iniciais do usuário para o avatar ──
  const userInitials = useMemo(() => {
    if (!userName) return '?';
    return userName.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  }, [userName]);

  return (
    <>
      {/* CSS global do Navbar */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        @keyframes dropdownFadeIn {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes mobileMenuSlide {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .app-navbar {
          font-family: 'Inter', 'Segoe UI', sans-serif;
        }

        .nav-tab-btn {
          position: relative;
          padding: 0 4px;
          height: 56px;
          display: flex;
          align-items: center;
          border: none;
          background: none;
          cursor: pointer;
          font-size: calc(13px * var(--font-scale))
          font-weight: 500;
          color: rgba(255,255,255,0.75);
          transition: color 0.15s;
          white-space: nowrap;
          font-family: 'Inter', 'Segoe UI', sans-serif;
          text-decoration: none;
        }
        .nav-tab-btn:hover {
          color: #fff;
        }
        .nav-tab-btn.active {
          color: #fff;
          font-weight: 600;
        }
        
        .desktop-nav-tabs {
          white-space: nowrap;
        }

        .nav-more-btn {
          gap: 2px;
        }

        .nav-more-btn span {
          display: block;
          max-width: 160px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .nav-tab-btn.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: #fff;
          border-radius: 3px 3px 0 0;
        }

        .nav-icon-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1px solid rgba(255,255,255,0.2);
          background: rgba(255,255,255,0.1);
          color: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 14px;
          transition: background 0.15s;
          flex-shrink: 0;
        }
        .nav-icon-btn:hover {
          background: rgba(255,255,255,0.2);
        }

        .mobile-nav-tab {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 16px;
          font-size: 14px;
          font-weight: 500;
          color: #374151;
          border-radius: 8px;
          cursor: pointer;
          transition: background 0.1s;
          text-decoration: none;
          font-family: 'Inter', 'Segoe UI', sans-serif;
        }
        .mobile-nav-tab:hover {
          background: #f3f4f6;
        }
        .mobile-nav-tab.active {
          background: #eff6ff;
          color: #2563eb;
          font-weight: 600;
        }
      `}</style>

      {/* ── NAVBAR PRINCIPAL ── */}
      <nav
        className="app-navbar"
        style={{
          position: 'fixed',
          top: 'var(--subscription-banner-height, 0px)',
          left: 0,
          right: 0,
          height: '56px',
          background: theme.gradient,
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          padding: '0 16px',
          gap: '8px',
          boxShadow: '0 2px 16px rgba(0,0,0,0.2)',
        }}
      >
        {/* ── Logo ── */}
        <Link
          to={tabs.length > 0 ? `/${tabs[0].key}` : '/'}
          style={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginRight: '8px' }}
        >
          <img src={logoFxcImage} alt="Logo" style={{ height: '32px', width: 'auto' }} />
        </Link>

        {/* ── Divider vertical ── */}
        <div style={{ width: '1px', height: '28px', backgroundColor: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

        {/* ── Info da clínica + usuário (desktop) ── */}
        <div style={{
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          marginRight: '16px', flexShrink: 0,
        }} className="d-none d-lg-flex">
          <div style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '700', color: '#fff', lineHeight: '1.2' }}>
            {currentEntidade?.nome || 'Clínica'}
          </div>
          <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: 'rgba(255,255,255,0.65)', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '9px' }} />
              {formattedDate}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
              <FontAwesomeIcon icon={faClock} style={{ fontSize: '9px' }} />
              {formattedTime}
            </span>
          </div>
        </div>

        {/* ── Divider vertical (desktop) ── */}
        <div style={{ width: '1px', height: '28px', backgroundColor: 'rgba(255,255,255,0.2)', flexShrink: 0 }} className="d-none d-lg-block" />

        {/* ── Abas de navegação (desktop) ── */}
        <div
          className="d-none d-lg-flex desktop-nav-tabs"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '2px',
            flex: 1,
            minWidth: 0,
            padding: '0 8px',
            overflow: 'visible',
          }}
        >
          {visibleDesktopTabs.map(tab => (
            <button
              key={tab.key}
              className={`nav-tab-btn ${activeHeaderTabKey === tab.key ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.key)}
              style={{ padding: '0 14px' }}
            >
              {tab.title}
            </button>
          ))}

          {overflowDesktopTabs.length > 0 && (
            <NavDropdown
              align="left"
              trigger={
                <button
                  type="button"
                  className={`nav-tab-btn nav-more-btn ${activeOverflowTab ? 'active' : ''}`}
                  style={{ padding: '0 12px' }}
                >
                  <span>
                    {activeOverflowTab
                      ? `Mais · ${activeOverflowTab.title}`
                      : 'Mais'
                    }
                  </span>

                  <FontAwesomeIcon
                    icon={faChevronDown}
                    style={{
                      marginLeft: '6px',
                      fontSize: '9px',
                      opacity: 0.75,
                    }}
                  />
                </button>
              }
            >
              <div style={{ padding: '8px 0' }}>
                <div style={{
                  padding: '6px 16px 8px',
                  fontSize: `calc(11px * var(--font-scale))`,
                  fontWeight: '700',
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  Outros módulos
                </div>

                {overflowDesktopTabs.map(tab => (
                  <DropdownItem
                    key={tab.key}
                    label={tab.title}
                    onClick={() => handleTabClick(tab.key)}
                  />
                ))}
              </div>
            </NavDropdown>
          )}
        </div>

        {/* ── Ações à direita (desktop) ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: 'auto', flexShrink: 0 }}
             className="d-none d-lg-flex">

          <FontSizeControl />

          {/* Divider visual entre o controle e o badge */}
          <div style={{ width: '1px', height: '20px', backgroundColor: 'rgba(255,255,255,0.2)' }} />

          {/* Badge do perfil */}
          <div style={{
            padding: '3px 10px', borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.15)',
            fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', color: '#fff',
            border: '1px solid rgba(255,255,255,0.25)',
          }}>
            {currentUserRole}
          </div>

          {/* Comunicados */}
          {canViewComunicados && (
            <NavDropdown
              trigger={
                <div className="nav-icon-btn" title="Comunicados">
                  <FontAwesomeIcon icon={faMessage} style={{ fontSize: `calc(13px * var(--font-scale))` }} />
                </div>
              }
            >
              <div style={{ padding: '8px 0' }}>
                <div style={{ padding: '6px 16px 8px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Mensagens
                </div>
                <DropdownItem to="/comunicados" icon={faMessage} label="Comunicados" />
              </div>
            </NavDropdown>
          )}

          {/* Configurações */}
          {canViewConfiguracoes && (
            <NavDropdown
              trigger={
                <div className="nav-icon-btn" title="Configurações">
                  <FontAwesomeIcon icon={faCog} style={{ fontSize: `calc(13px * var(--font-scale))` }} />
                </div>
              }
            >
              <div style={{ padding: '8px 0' }}>
                <div style={{ padding: '6px 16px 8px', fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Sistema
                </div>
                <DropdownItem to="/configuracoes" icon={faCog} label="Configurações" />
                {currentUserRole === 'Representante Legal' && (
                  <DropdownItem to="/fluxopay" icon={faWallet} label="Fluxopay" />
                )}
              </div>
            </NavDropdown>
          )}

          {/* Avatar + Menu do usuário */}
          <NavDropdown
            trigger={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', padding: '4px 8px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'rgba(255,255,255,0.1)', transition: 'background 0.15s' }}
                   onMouseEnter={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.2)'}
                   onMouseLeave={e => e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'}
              >
                {/* Avatar */}
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%',
                  background: 'rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: `calc(11px * var(--font-scale))`, fontWeight: '700', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.4)',
                  flexShrink: 0,
                }}>
                  {userInitials}
                </div>
                <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '500', color: '#fff', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {userName}
                </span>
                <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: `calc(10px * var(--font-scale))`, color: 'rgba(255,255,255,0.7)' }} />
              </div>
            }
          >
            {/* Header do dropdown */}
            <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                  background: theme.gradient,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#fff',
                }}>
                  {userInitials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '700', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {userName}
                  </div>
                  <span style={{
                    fontSize: `calc(10px * var(--font-scale))`, fontWeight: '600', padding: '1px 6px', borderRadius: '999px',
                    backgroundColor: theme.badge, color: theme.badgeText,
                  }}>
                    {currentUserRole}
                  </span>
                </div>
              </div>
              <div style={{ marginTop: '8px', fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FontAwesomeIcon icon={faBuilding} style={{ fontSize: `calc(10px * var(--font-scale))` }} />
                {currentEntidade?.nome || 'N/A'}
              </div>
            </div>

            {/* Itens */}
            <div style={{ padding: '8px 0' }}>
              {currentUserRole === 'Representante Legal' && (
                <DropdownItem to="/configuracoes" icon={faBuilding} label="Entidade" />
              )}
              <DropdownItem to="/perfil" icon={faIdBadge} label="Meu Perfil" />
              <DropdownItem to="/suporte" icon={faHeadset} label="Central de Atendimento" />
              <DropdownDivider />
              <DropdownItem onClick={onLogout} icon={faSignOutAlt} label="Sair" danger />
            </div>
          </NavDropdown>
        </div>

        {/* ── Lado direito mobile ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}
             className="d-flex d-lg-none">
          {/* Nome da clínica em mobile */}
          <span style={{ fontSize: `calc(12px * var(--font-scale))`, fontWeight: '600', color: 'rgba(255,255,255,0.9)', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {currentEntidade?.nome || 'Painel'}
          </span>

          {/* Botão hamburguer */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              width: '36px', height: '36px', borderRadius: '8px',
              border: '1px solid rgba(255,255,255,0.2)',
              backgroundColor: mobileMenuOpen ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)',
              color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: `calc(15px * var(--font-scale))`, transition: 'background 0.15s',
            }}
          >
            <FontAwesomeIcon icon={mobileMenuOpen ? faTimes : faBars} />
          </button>
        </div>
      </nav>

      {/* ── MENU MOBILE (dropdown abaixo da navbar) ── */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'fixed', inset: 0, top: '56px',
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 1048,
              backdropFilter: 'blur(2px)',
            }}
          />

          {/* Painel do menu */}
          <div style={{
            position: 'fixed', top: '56px', left: 0, right: 0,
            backgroundColor: '#fff',
            zIndex: 1049,
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
            borderRadius: '0 0 16px 16px',
            animation: 'mobileMenuSlide 0.2s ease',
            maxHeight: 'calc(100vh - 56px)',
            overflowY: 'auto',
          }}>
            {/* Header com info do usuário */}
            <div style={{
              padding: '16px',
              background: theme.gradient,
              display: 'flex', alignItems: 'center', gap: '12px',
            }}>
              <div style={{
                width: '44px', height: '44px', borderRadius: '50%', flexShrink: 0,
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '16px', fontWeight: '700', color: '#fff',
                border: '2px solid rgba(255,255,255,0.4)',
              }}>
                {userInitials}
              </div>
              <div>
                <div style={{ fontSize: `calc(14px * var(--font-scale))`, fontWeight: '700', color: '#fff' }}>{userName}</div>
                <div style={{ fontSize: `calc(11px * var(--font-scale))`, color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>
                  {currentEntidade?.nome}
                </div>
                <span style={{
                  fontSize: `calc(10px * var(--font-scale))`, fontWeight: '600', padding: '1px 8px', borderRadius: '999px',
                  backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.3)', marginTop: '4px', display: 'inline-block',
                }}>
                  {currentUserRole}
                </span>
              </div>
            </div>

            {/* Abas de navegação */}
            <div style={{ padding: '12px' }}>
              <div style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', paddingLeft: '8px' }}>
                Navegação
              </div>
              {tabs.map(tab => (
                <div
                  key={tab.key}
                  className={`mobile-nav-tab ${activeHeaderTabKey === tab.key ? 'active' : ''}`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  {tab.title}
                </div>
              ))}
            </div>

            {/* Divider */}
            <div style={{ height: '1px', backgroundColor: '#f3f4f6', margin: '0 12px' }} />

            {/* Ações */}
            <div style={{ padding: '12px' }}>
              <div style={{ fontSize: `calc(10px * var(--font-scale))`, fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px', paddingLeft: '8px' }}>
                Ações
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '10px 8px',
                borderRadius: '8px',
                marginBottom: '4px',
              }}>
                <span style={{ fontSize: `calc(13px * var(--font-scale))`, fontWeight: '500', color: '#374151' }}>
                  Tamanho da fonte
                </span>
                <FontSizeControl />
              </div>

              {canViewComunicados && (
                <Link to="/comunicados" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                  <FontAwesomeIcon icon={faMessage} style={{ width: '16px', color: '#9ca3af' }} />
                  Comunicados
                </Link>
              )}

              {canViewConfiguracoes && (
                <Link to="/configuracoes" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                  <FontAwesomeIcon icon={faCog} style={{ width: '16px', color: '#9ca3af' }} />
                  Configurações
                </Link>
              )}

              {currentUserRole === 'Representante Legal' && (
                <Link to="/fluxopay" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                  <FontAwesomeIcon icon={faWallet} style={{ width: '16px', color: '#0a8a70' }} />
                  Fluxopay
                </Link>
              )}

              {currentUserRole === 'Representante Legal' && (
                <Link to="/configuracoes" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                  <FontAwesomeIcon icon={faBuilding} style={{ width: '16px', color: '#9ca3af' }} />
                  Entidade
                </Link>
              )}

              <Link to="/suporte" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                <FontAwesomeIcon icon={faHeadset} style={{ width: '16px', color: '#9ca3af' }} />
                Central de Atendimento
              </Link>

              <Link to="/perfil" className="mobile-nav-tab" onClick={() => setMobileMenuOpen(false)}>
                <FontAwesomeIcon icon={faIdBadge} style={{ width: '16px', color: '#9ca3af' }} />
                Meu Perfil
              </Link>
            </div>

            {/* Divider */}
            <div style={{ height: '1px', backgroundColor: '#f3f4f6', margin: '0 12px' }} />

            {/* Logout */}
            <div style={{ padding: '12px' }}>
              <div
                className="mobile-nav-tab"
                onClick={() => { onLogout(); setMobileMenuOpen(false); }}
                style={{ color: '#ef4444' }}
              >
                <FontAwesomeIcon icon={faSignOutAlt} style={{ width: '16px', color: '#ef4444' }} />
                Sair
              </div>
            </div>

            {/* Info de login */}
            <div style={{ padding: '10px 16px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
              <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FontAwesomeIcon icon={faCalendar} style={{ fontSize: `calc(10px * var(--font-scale))` }} />
                {formattedDate}
              </span>
              <span style={{ fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: `calc(10px * var(--font-scale))` }} />
                {formattedTime}
              </span>
            </div>
          </div>
        </>
      )}

      {/* Espaçador para compensar a navbar fixed */}
      <div style={{ height: '56px' }} />
    </>
  );
};

export default AppNavbar;
